/**
 * Internal verification namespace guardrails.
 *
 * The product has one runtime (`real`). Demo is an ordinary workspace whose
 * bundled records are loaded into that runtime. The legacy `demo:` namespace
 * remains only for isolated fixtures and their cross-namespace safety tests;
 * it is not selectable client state or URL transport.
 */

export const VERIFICATION_ENVIRONMENTS = ["demo", "real"] as const;
export type VerificationEnvironment = (typeof VERIFICATION_ENVIRONMENTS)[number];
/** Missing legacy API scope resolves to the only product runtime: real. */
export const DEFAULT_VERIFICATION_ENVIRONMENT: VerificationEnvironment = "real";

export const VERIFICATION_ENV_HEADER = "x-excellent-verification-environment";
export const VERIFICATION_ENV_QUERY = "env";
export const VERIFICATION_ENV_STORAGE_KEY = "excellent.verification.environment";
export const PRODUCT_LOCAL_ENVIRONMENT_LABEL = "This Mac";
export const SAMPLE_FIXTURE_ENVIRONMENT_LABEL = "Sample fixture";

export const DEMO_ID_PREFIX = "demo:";
export const REAL_ID_PREFIX = "real:";

/** Surfaces the server must scope to the active environment (PRD T3). */
export const VERIFICATION_ENV_SURFACES = [
  "read",
  "write",
  "search",
  "receipts",
  "packs",
  "downloads",
  "audit-room",
] as const;
export type VerificationEnvSurface = (typeof VERIFICATION_ENV_SURFACES)[number];

export type EnvProvenance = {
  environment: VerificationEnvironment;
  /** Demo trust artifacts are visibly demo-scoped. */
  demoScoped: boolean;
  /** Structurally incapable of entering the opposite trust chain. */
  trustChain: VerificationEnvironment;
};

export type EnvEnforcement =
  | { ok: true; environment: VerificationEnvironment; provenance: EnvProvenance }
  | {
      ok: false;
      reason:
        | "environment-missing"
        | "environment-invalid"
        | "environment-mismatch"
        | "unmarked-id"
        | "surface-unscoped";
      environment: VerificationEnvironment | null;
      surface: string;
    };

export function isVerificationEnvironment(value: unknown): value is VerificationEnvironment {
  return value === "demo" || value === "real";
}

export function productEnvironmentLabel(environment: VerificationEnvironment): string {
  return environment === "demo"
    ? SAMPLE_FIXTURE_ENVIRONMENT_LABEL
    : PRODUCT_LOCAL_ENVIRONMENT_LABEL;
}

export function parseVerificationEnvironment(value: unknown): VerificationEnvironment | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase();
  return isVerificationEnvironment(trimmed) ? trimmed : null;
}

export type EnvWorkspaceId = string | number | null;

export interface EnvPreferenceStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function storageKeyForWorkspace(workspaceId?: EnvWorkspaceId): string {
  const id = workspaceId == null ? "" : String(workspaceId).trim();
  return id ? `${VERIFICATION_ENV_STORAGE_KEY}:${id}` : VERIFICATION_ENV_STORAGE_KEY;
}

export function readPersistedEnvironment(
  store: EnvPreferenceStore,
  workspaceId?: EnvWorkspaceId,
): VerificationEnvironment | null {
  try {
    return parseVerificationEnvironment(store.getItem(storageKeyForWorkspace(workspaceId)));
  } catch {
    return null;
  }
}

/**
 * Written only when a person explicitly clears their choice in Settings.
 *
 * It is deliberately distinct from an absent key, and the difference is the
 * whole point: absent means "never chose", which adopts This Mac with no
 * chooser so a fresh profile is zero-setup; this sentinel means "ask me again",
 * which is the one path that still opens the chooser after a reload.
 */
export const VERIFICATION_ENV_CLEARED = "cleared";

export type EnvPreferenceState =
  | { kind: "absent" }
  | { kind: "cleared" }
  | { kind: "invalid"; raw: string }
  | { kind: "chosen"; environment: VerificationEnvironment };

/**
 * Three-way read. `readPersistedEnvironment` collapses absent, cleared and
 * unparseable into one `null`, which cannot distinguish a fresh profile from a
 * deliberate reset.
 */
export function readPersistedEnvironmentState(
  store: EnvPreferenceStore,
  workspaceId?: EnvWorkspaceId,
): EnvPreferenceState {
  let raw: string | null;
  try {
    raw = store.getItem(storageKeyForWorkspace(workspaceId));
  } catch {
    return { kind: "absent" };
  }
  if (raw == null) return { kind: "absent" };
  if (raw === VERIFICATION_ENV_CLEARED) return { kind: "cleared" };
  const parsed = parseVerificationEnvironment(raw);
  return parsed ? { kind: "chosen", environment: parsed } : { kind: "invalid", raw };
}

export function markPersistedEnvironmentCleared(
  store: EnvPreferenceStore,
  workspaceId?: EnvWorkspaceId,
): void {
  store.setItem(storageKeyForWorkspace(workspaceId), VERIFICATION_ENV_CLEARED);
}

export function writePersistedEnvironment(
  store: EnvPreferenceStore,
  environment: VerificationEnvironment,
  workspaceId?: EnvWorkspaceId,
): void {
  store.setItem(storageKeyForWorkspace(workspaceId), environment);
}

export function clearPersistedEnvironment(
  store: EnvPreferenceStore,
  workspaceId?: EnvWorkspaceId,
): void {
  store.removeItem(storageKeyForWorkspace(workspaceId));
}

export function provenanceFor(environment: VerificationEnvironment): EnvProvenance {
  return {
    environment,
    demoScoped: environment === "demo",
    trustChain: environment,
  };
}

export function stripEnvironmentPrefix(id: string): string {
  if (id.startsWith(DEMO_ID_PREFIX)) return id.slice(DEMO_ID_PREFIX.length);
  if (id.startsWith(REAL_ID_PREFIX)) return id.slice(REAL_ID_PREFIX.length);
  return id;
}

export function namespacedId(environment: VerificationEnvironment, rawId: string): string {
  return `${environment}:${stripEnvironmentPrefix(rawId)}`;
}

export function environmentOfId(id: string): VerificationEnvironment | "unmarked" {
  if (id.startsWith(DEMO_ID_PREFIX)) return "demo";
  if (id.startsWith(REAL_ID_PREFIX)) return "real";
  return "unmarked";
}

/**
 * Server-side (and contract-time) enforcement. Call this from reads, writes,
 * search, receipts, packs, downloads, and audit-room. Cross-environment
 * object ids and unmarked ids are refused.
 */
export function enforceVerificationEnvironment(input: {
  environment: unknown;
  objectIds?: readonly string[];
  surface: string;
}): EnvEnforcement {
  if (input.environment == null || input.environment === "") {
    return {
      ok: false,
      reason: "environment-missing",
      environment: null,
      surface: input.surface,
    };
  }
  const environment = parseVerificationEnvironment(input.environment);
  if (!environment) {
    return {
      ok: false,
      reason: "environment-invalid",
      environment: null,
      surface: input.surface,
    };
  }
  if (!(VERIFICATION_ENV_SURFACES as readonly string[]).includes(input.surface)) {
    return {
      ok: false,
      reason: "surface-unscoped",
      environment,
      surface: input.surface,
    };
  }
  for (const id of input.objectIds ?? []) {
    const marked = environmentOfId(id);
    if (marked === "unmarked") {
      return { ok: false, reason: "unmarked-id", environment, surface: input.surface };
    }
    if (marked !== environment) {
      return { ok: false, reason: "environment-mismatch", environment, surface: input.surface };
    }
  }
  return { ok: true, environment, provenance: provenanceFor(environment) };
}

/** Explicit legacy scope wins; ordinary product requests default to real. */
export function environmentFromRequest(input: {
  header?: string | null;
  query?: string | null;
}): VerificationEnvironment {
  return (
    parseVerificationEnvironment(input.header) ??
    parseVerificationEnvironment(input.query) ??
    DEFAULT_VERIFICATION_ENVIRONMENT
  );
}

/**
 * URL override is safe only when it does not mix with an object id from the
 * other environment. An override that conflicts with a namespaced id is ignored.
 */
export function isUrlOverrideSafe(input: {
  override: unknown;
  objectIds?: readonly string[];
}):
  | { ok: true; environment: VerificationEnvironment }
  | { ok: false; reason: "override-invalid" | "override-conflicts-with-object" } {
  const override = parseVerificationEnvironment(input.override);
  if (!override) return { ok: false, reason: "override-invalid" };
  for (const id of input.objectIds ?? []) {
    const marked = environmentOfId(id);
    if (marked !== "unmarked" && marked !== override) {
      return { ok: false, reason: "override-conflicts-with-object" };
    }
  }
  return { ok: true, environment: override };
}

/** Chrome detail ids are the second path segment (`/findings/:id`). */
export function pathWithoutObjectDetail(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const parts = normalized.split("/").filter(Boolean);
  if (parts.length >= 2 && parts[1] && parts[1] !== "new") {
    return `/${parts[0]}`;
  }
  return normalized.startsWith("/") ? normalized : `/${normalized}`;
}

export function objectIdsFromPathname(pathname: string): string[] {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const parts = normalized.split("/").filter(Boolean);
  if (parts.length >= 2 && parts[1] && parts[1] !== "new") return [parts[1]];
  return [];
}

export function shouldClearObjectDetail(input: {
  from: VerificationEnvironment;
  to: VerificationEnvironment;
  pathname: string;
}): boolean {
  if (input.from === input.to) return false;
  const current = input.pathname.replace(/\/+$/, "") || "/";
  return pathWithoutObjectDetail(input.pathname) !== current;
}

export function resolveActiveEnvironment(input: {
  persisted: VerificationEnvironment | null;
  urlOverride?: unknown;
  objectIds?: readonly string[];
}): {
  environment: VerificationEnvironment | null;
  source: "persisted" | "url" | "none";
  urlOverrideApplied: boolean;
} {
  const override = parseVerificationEnvironment(input.urlOverride);
  if (override) {
    const safe = isUrlOverrideSafe({ override, objectIds: input.objectIds });
    if (safe.ok) {
      return { environment: safe.environment, source: "url", urlOverrideApplied: true };
    }
  }
  if (input.persisted) {
    return { environment: input.persisted, source: "persisted", urlOverrideApplied: false };
  }
  return { environment: null, source: "none", urlOverrideApplied: false };
}
